import React from 'react';
import { Target, Eye, Heart, Award, Users, Building, TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent, stats } from '../data/mock';

const AboutPage = () => {
  const valueIcons = {
    Excelencia: Award,
    'Innovación': TrendingUp,
    Compromiso: Heart,
    Integridad: Shield,
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-slate-900">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&q=80"
            alt="Corporate Building"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-slate-900" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Conoce <span className="text-sky-400">Corporativo MK</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Más de 15 años transformando la manera en que las empresas trabajan, 
              ofreciendo espacios que inspiran productividad y éxito.
            </p>
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-6">
                Nuestra Historia
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {aboutContent.history}
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Hoy, continuamos innovando y expandiendo nuestra oferta para adaptarnos 
                a las nuevas formas de trabajo, siempre con el compromiso de ofrecer 
                la máxima calidad y servicio a nuestros clientes.
              </p>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1512403754473-27835f7b9984?w=800&q=80"
                  alt="Our Building"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-sky-500 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-sky-500" />
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center mb-6">
                  <Target className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Nuestra Misión</h3>
                <p className="text-slate-600 leading-relaxed text-lg">
                  {aboutContent.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-0 shadow-xl overflow-hidden">
              <div className="h-2 bg-amber-500" />
              <CardContent className="p-10">
                <div className="w-16 h-16 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-4">Nuestra Visión</h3>
                <p className="text-slate-600 leading-relaxed text-lg">
                  {aboutContent.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Nuestros Valores
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Los principios que guían cada decisión y acción en Corporativo MK.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.values.map((value, index) => {
              const Icon = valueIcons[value.title] || Award;
              return (
                <div
                  key={index}
                  className="group text-center p-8 rounded-2xl bg-slate-50 hover:bg-sky-500 transition-all duration-300"
                >
                  <div className="w-16 h-16 rounded-2xl bg-sky-100 text-sky-600 group-hover:bg-white group-hover:text-sky-500 flex items-center justify-center mx-auto mb-6 transition-all duration-300">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 group-hover:text-white mb-3 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 group-hover:text-sky-100 transition-colors duration-300">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Corporativo MK en Números
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Resultados que hablan por sí mismos.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 rounded-2xl bg-slate-800/50 border border-slate-700"
              >
                <div className="text-4xl lg:text-5xl font-bold text-sky-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Highlight */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&q=80"
                  alt="Our Team"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-sky-100 rounded-2xl -z-10" />
            </div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-6">
                Un Equipo Comprometido
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Detrás de Corporativo MK hay un equipo de profesionales apasionados 
                por crear los mejores espacios de trabajo. Desde arquitectos e 
                interioristas hasta expertos en atención al cliente, cada miembro 
                aporta su talento para garantizar una experiencia excepcional.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Nuestro compromiso va más allá de ofrecer un espacio físico; 
                buscamos ser socios estratégicos en el crecimiento de tu negocio.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
